import { UseMutateFunction } from "@tanstack/react-query";
import { AxiosError } from "axios";
import * as yup from "yup";

export type Personel = {
  id: string;
  email: string;
  password?: string;
  fullname: string;
  username: string;
  date_of_start: Date;
  salary_net: number;
  salary_gross: number;
  phone_number: string;
};

export interface CustomerDeleteModalProps {
  isModalOpen: boolean;
  toggle: () => void;
  modalType?: string;
  userData?: any;
}

export interface PaymentDeleteModalProps {
  isModalOpen: boolean;
  toggle: () => void;
  modalType?: string;
  modalOptions?: any;
  mutate: UseMutateFunction<
    CustomerAllPaymentResponse,
    AxiosError<any>,
    CustomerAllPaymentRequest,
    unknown
  >;
}

export interface CustomerMaintenanceHistoryModalProps {
  isModalOpen: boolean;
  toggle: () => void;
}

export interface CustomerPaymentHistoryModalProps {
  isModalOpen: boolean;
  toggle: () => void;
  paymentId: number;
  mutate: UseMutateFunction<
    CustomerAllPaymentResponse,
    AxiosError<any>,
    CustomerAllPaymentRequest,
    unknown
  >;
}

export interface CustomerProfileDetailRequest {
  id: string;
}

interface Customer {
  id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  fullname: string;
  email: string | null;
  description: string;
  phone_number: string;
  country: string;
  district: string;
  neighbourhood: string;
  street: string;
  flat_number: string;
  lang: string;
  long: string;
  address_description: string;
  company: number;
  person_who_created: number;
}

interface MaintenanceMaterials {
  id: string;
  device_part: {
    id: string;
    name: string;
    description: string;
  };
  operation_type:
    | "assembly"
    | "dismantle"
    | "replace"
    | "repair"
    | "no_operation";
}

interface Maintenance {
  id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  procedure: string;
  note: string | null;
  maintenance_date: string | null;
  next_maintenance_date: string;
  maintenance_type: string;
  customer: Customer;
  remaining_days: string;
  person_who_maintain: number | null;
  maintenance_materials: MaintenanceMaterials[];
}

export interface CustomerDevice {
  id: number;
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
  name: string;
  description: string;
  company: number;
}

export interface CustomerProfileResponse {
  id: number;
  remaining_days: null;
  detail: string;
  customer_devices: {
    id: number;
    device: {
      id: number;
      is_active: boolean;
      created_at: Date;
      updated_at: Date;
      name: string;
      description: number;
      warranty_period: number;
      system: "open" | "close" | string;
      company: {
        id: 1;
        is_active: boolean;
        created_at: Date;
        updated_at: Date;
        name: string;
        phone_number1: string;
        phone_number2: string;
        phone_number3: string;
        address: string;
        email: string;
        licence_type: "both";
        license_start_date: Date;
        license_expire_date: Date;
      };
      device_part: CustomerDevice[];
    };
    date_of_setup: Date;
    date_of_maintenance: Date;
    warranty_start: Date;
    warranty_end: Date;
    description: string;
    first_setup: boolean;
  }[];
  fullname: string;
  description: string;
  phone_number: string;
  phone_number2: string;
  phone_number3: string;
  email: string;
  country: string;
  district: string;
  neighbourhood: string;
  street: string;
  flat_number: string;
  lang: string;
  long: string;
  address_description: string;
  maintenances: Maintenance[];
  status: number;
  is_visible: boolean;
  sms_returned_back: boolean;
}

export interface CustomerPaymentRequest {
  customer: number;
  device: number;
  name_of_payment: string;
  note: string;
  amount: number;
  payment_type: string;
  number_of_installment: number;
  date_of_installment: string;
  got_amount: number;
  next_payment_date?: string;
}

export interface CustomerLastMaintenanceRequest {
  id: number;
}

export interface CustomerAllMaintenanceRequest {
  id: number;
}

export interface CustomerAllPaymentRequest {
  id: number;
}

export interface CustomerAllPaymentOrdersRequest {
  id: number;
}

export interface CustomerAllPaymentRecord {
  id: number;
  customer: number;
  device: number;
  name_of_payment: string;
  note: string;
  amount: number;
  payment_type: string;
  number_of_installment: number;
  date_of_installment: string;
  total_paid: string;
  retrieval: string;
  payment_date: string;
  is_paid: boolean;
  paid_date: string;
  person_who_created: {
    id: number;
    email: string;
    fullname: string;
    username: string;
    date_of_start: string;
    salary_net: string;
    salary_gross: string;
    phone_number: string;
  };
}

export interface CustomerAllPaymentResponse {
  count: number;
  next: string;
  previous: string;
  results: CustomerAllPaymentRecord[];
}

interface MaintenanceMaterial {
  id: number;
  device_part: {
    id: number;
    name: string;
    description: string;
  };
  operation_type: string;
}

interface PersonWhoMaintain {
  id: number;
  email: string;
  fullname: string;
  username: string;
  date_of_start: string;
  salary_net: number;
  salary_gross: number;
  phone_number: string | null;
}

interface MaintenanceRecord {
  id: number;
  customer_device: number;
  customer_device_name: string;
  person_who_maintain: PersonWhoMaintain;
  procedure: string;
  note: string;
  maintenance_date: string;
  next_maintenance_date: string;
  maintenance_materials: MaintenanceMaterial[];
}

export interface CustomerAllMaintenanceResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MaintenanceRecord[];
}

export interface DevicePartOperation {
  id: number;
  customer_device: number;
  customerId: string;
  procedure: string;
  note: string;
  maintenanceDate: Date;
  next_maintenance_date: Date;
  device_parts: {
    device_part: number;
    operation_type:
      | "assembly"
      | "dismantle"
      | "replace"
      | "repair"
      | "no_operation";
  }[];
}

interface DevicePartMaintanence {
  device_part_id: number;
  name: string;
  description: string;
}

interface DevicePartExtentsOperation {
  device_part: DevicePartMaintanence;
  operation_type: string;
}

export interface CustomerLastMaintenanceResponse
  extends Array<DevicePartExtentsOperation> {}

export interface CustomerPaymentResponse {
  id: number;
  customer: number;
  device: number;
  name_of_payment: string;
  note: string;
  amount: number;
  payment_type: string;
  number_of_installment: number;
  date_of_installment: string;
}

export const payType = {
  CASH: "cash",
  TRANSFER: "transfer",
  CREDIT_CARD: "credit_card",
  CHECK: "check",
  UNKNOWN: "unknown",
};

export const OperationTypes = {
  ADD: "assembly",
  CHANGE: "replace",
  REPLACE: "back",
  REPAIR: "repair",
  NO_OPERATION: "no_operation",
  DELETE: "dismantle",
};

export const getOperationTypes = (operationTypes: string) => {
  if (operationTypes === OperationTypes.ADD) return "Eklendi";
  if (operationTypes === OperationTypes.CHANGE) return "Değiştirildi";
  if (operationTypes === OperationTypes.REPLACE) return "Geri AL";
  if (operationTypes === OperationTypes.REPAIR) return "Onarıldı";
  if (operationTypes === OperationTypes.NO_OPERATION) return "İşlem yapılmadı";
  if (operationTypes === OperationTypes.DELETE) return "Çıkarıldı";
};

export interface CustomerMaintenanceRequest {
  id: number;
  customer_device: number;
  customerId: string;
  procedure: string;
  note: string;
  maintenanceDate: Date;
  next_maintenance_date: Date;
  device_parts: {
    device_part: number;
    operation_type:
      | "assembly"
      | "dismantle"
      | "replace"
      | "repair"
      | "no_operation";
  }[];
}

export interface CustomerMaintenanceUpdateRequest {
  maintenanceId: number;
  customer_device: number;
  procedure: string;
  note: string;
  next_maintenance_date: Date;
}

export interface CustomerWarrantyUpdateRequest {
  customerDeviceId: number;
  deviceId: number;
  warranty_start: string;
  warranty_end: string;
}

export interface CustomerPaymentUpdateRequest {
  paymentId: string;
  is_visible: boolean;
  is_active: boolean;
}

export interface CustomerPaymentAddRequest {
  paymentId?: number;
  payment?: number;
  amount?: number;
  payment_type: number;
  is_paid?: boolean;
  is_partial?: boolean;
  payment_date?: string;
  next_payment_date?: string;
}

const today = new Date();
today.setHours(0, 0, 0, 0);

export const customerMaintenanceSchema = yup.object({
  procedure: yup.string().required("Ödeme adı boş bırakılamaz"),
  next_maintenance_date: yup
    .date()
    .min(today, "Tarih bugünden eski olamaz")
    .required("Tarih boş bırakılamaz"),
});

export const paymentDetailSchema = (maxAmount: number) =>
  yup.object({
    amount: yup
      .number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue.trim() === "" ? null : value
      )
      .required("Ödeme  boş bırakılamaz")
      .max(maxAmount, `Ödeme miktarı kalan tutar değerinden büyük olamaz`)
      .min(1, `Ödeme miktarı 1'dan küçük olamaz`),
  });

export const paymentModalschema = yup.object({
  name_of_payment: yup.string().required("Ödeme adı boş bırakılamaz"),
  amount: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required("Yapılan iş ücreti boş bırakılamaz"),
  got_amount: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .max(yup.ref("amount"), "Alınan ücret yapılan iş ücretinden fazla olamaz"),
  payment_type: yup.string().notRequired(),
  number_of_installment: yup
    .number()
    .when("payment_type", (payment_type: any, schema) => {
      return payment_type[0] === "ip"
        ? schema
            .required("Taksit sayısı boş bırakılamaz")
            .min(2, "Taksit sayısı 1'dan büyük olmalıdır")
        : schema.notRequired();
    }),
});

export const paymentFormatter = (value: string) => {
  if (value == "1") return "Nakit";
  if (value == "2") return "Transfer";
  if (value == "3") return "Kredi Kartı";
  if (value == "4") return "Çek";
  return "Bilinmiyor";
};
